import { createRouter, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'
import {
  findNavigationItemByPath,
  isNavigationPathAccessibleToProjectViewer,
  isNavigationPathRequiresSystemAdmin,
  isNavigationPathRequiresProjectOwnerOrAdmin,
} from '@/shared/config/navigation'
import { useAuth } from '@/shared/composables/useAuth'
import { useAssetHierarchyInitialization } from '@/features/asset-hierarchy/composables/useAssetHierarchyInitialization'
import { isProjectViewerRole, isSystemAdminRole, isProjectOwnerOrAdminRole, resolveEffectiveUserRole } from '@/shared/utils/projectAccess'
import { resolveProjectContext } from '@/shared/utils/projectUrl'
import { useProjectStore } from './shared/stores/project'
import { useNavigationIntentStore } from '@/shared/stores/navigationIntent'
import { useRecentNavigationStore } from '@/shared/stores/recentNavigation'
import { logger } from '@/shared/services/logging'

/** SessionStorage key for init redirect warning */
const INIT_REDIRECT_WARNING_KEY = 'adam:initRedirectWarning'

export interface InitRedirectWarning {
  summary: string
  detail: string
  originalPath: string
}

// Ensure there's always a route matching '/' so Vue Router doesn't warn during
// initial resolution. We use a minimal inline component that renders the
// default router view; navigation guard below will still redirect logged-in
// users to /home.
const appRoutes = [
  {
    path: '/',
    name: 'Root',
    meta: {
      requiresProject: false,
    },
    // lightweight functional component to satisfy the router at install time
    component: { template: '<router-view />' },
  },
  ...routes,
]

const router = createRouter({
  history: createWebHistory(),
  routes: appRoutes,
})

const navigateToProjectHome = async () => {
  const projectStore = useProjectStore();
  await projectStore.initialize();

  if (await projectStore.validateSelectedProjectExists()) {
    await router.push({ path: '/home' });
  } else {
    await router.push({ path: '/home/project-selection' });
  }
}

// Global navigation guard for authentication
router.beforeEach(async (to, from) => {
  const auth = useAuth();
  const navigationIntent = useNavigationIntentStore();
  const projectStore = useProjectStore();
  
  // Always ensure auth is initialized
  await auth.initialize();

  if (auth.isAuthenticated.value && projectStore.selectedProjectId) {
    await projectStore.validateSelectedProjectExists();
  }

  // After login (and on authenticated navigations), ensure the cached selected project still exists,
  // but only for routes that require a selected project.
  if (auth.isAuthenticated.value) {
    const requiresProject = to.meta.requiresProject !== false;
    if (requiresProject) {
      // Project context: the URL's projectId wins over the persisted (sessionStorage) selection,
      // and the URL always ends up carrying the selected project (self-heal on missing values).
      const projectContext = await resolveProjectContext(to, projectStore, {
        onInvalidSelection: () => navigationIntent.clearIntendedRoute(),
      });
      if (projectContext !== true) {
        return projectContext;
      }
    }
  }

  const isLoginRoute = to.path === '/login';
  if (!auth.isAuthenticated.value && !isLoginRoute) {
    navigationIntent.setIntendedRoute(to.fullPath);
  }

  // Handle root path redirection
  if (to.path === '/') {
    if (auth.isAuthenticated.value) {
      return await navigateToProjectHome();
    } else {
      return { path: '/login' };
    }
  }

  // Skip auth check for login page and demos
  if (isLoginRoute) {
    // If already authenticated and going to login, redirect to home
    if (auth.isAuthenticated.value) {
      navigationIntent.clearIntendedRoute();
      return await navigateToProjectHome();
    }
    return true;
  }

  // For all other routes, require authentication
  if (!auth.isAuthenticated.value) {
    return { path: '/login' };
  }

  if (to.meta.notFound === true) {
    return true;
  }

  const selectedProject = projectStore.getProjectById(projectStore.selectedProjectId);
  const effectiveUserRole = resolveEffectiveUserRole({
    projects: selectedProject ? [selectedProject] : projectStore.projects,
    selectedProjectId: projectStore.selectedProjectId,
    systemRole: auth.user.value?.systemRole ?? null,
    userId: auth.profile.value?.id || auth.user.value?.id || null,
  });

  if (isProjectViewerRole(effectiveUserRole)) {
    // Redirect create/new BoM routes to the list page
    if (
      to.path === '/spares/billofmaterials/new' ||
      to.path === '/spares/billofmaterials/create'
    ) {
      return { path: '/spares/billofmaterials' };
    }

    if (!isNavigationPathAccessibleToProjectViewer(to.path)) {
      return await navigateToProjectHome();
    }
  }

  // Block non-admins from system-admin-only routes
  if (isNavigationPathRequiresSystemAdmin(to.path) && !isSystemAdminRole(effectiveUserRole)) {
    return await navigateToProjectHome();
  }

  // Block users without project-owner or admin rights from owner-restricted routes
  if (isNavigationPathRequiresProjectOwnerOrAdmin(to.path) && !isProjectOwnerOrAdminRole(effectiveUserRole)) {
    return await navigateToProjectHome();
  }

  // Check initialization for asset-hierarchy routes
  if (to.path.startsWith('/asset-hierarchy') && to.path !== '/asset-hierarchy/initialize') {
    const initialization = useAssetHierarchyInitialization();
    const needsInit = await initialization.checkInitializationNeeded();
    
    if (needsInit) {
      // Store warning for display after redirect and log the event
      const warning: InitRedirectWarning = {
        summary: 'Redirect Notice',
        detail: `You were redirected because the asset hierarchy needs initialization.`,
        originalPath: to.fullPath
      };
      
      sessionStorage.setItem(INIT_REDIRECT_WARNING_KEY, JSON.stringify(warning));
      logger.warn('Asset hierarchy initialization required, redirecting', {
        originalPath: to.fullPath,
        fromPath: from.fullPath
      });
      
      return { path: '/asset-hierarchy/initialize' };
    }
  }

  return true;
});

/**
 * Retrieves and clears the init redirect warning from sessionStorage.
 * Call this in the initialize page's onMounted to display the warning toast.
 */
export function consumeInitRedirectWarning(): InitRedirectWarning | null {
  const warningJson = sessionStorage.getItem(INIT_REDIRECT_WARNING_KEY);
  if (!warningJson) {
    return null;
  }
  
  sessionStorage.removeItem(INIT_REDIRECT_WARNING_KEY);
  
  try {
    return JSON.parse(warningJson) as InitRedirectWarning;
  } catch {
    return null;
  }
}

// Set page title from route meta
router.afterEach((to, _from, failure) => {
  // A cancelled/failed navigation (e.g. a guard redirect via nested push) must not overwrite the title or recents.
  if (failure) {
    return;
  }

  const recentNavigationStore = useRecentNavigationStore();
  const navigationItem = findNavigationItemByPath(to.path);

  if (to.meta.notFound !== true && navigationItem) {
    recentNavigationStore.addRecentItem(navigationItem.id);
  }

  const defaultTitle = 'ADAM Cloud';
  // Vue Router 4: meta is Record<string, unknown>, so cast to string if present
  const title = typeof to.meta?.title === 'string' ? to.meta.title : defaultTitle;
  document.title = title;
});

export default router;
